import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { C } from '../albumColors';
import UserSearchCard from './cards/UserSearchCard';
import SmartTradeCard from './cards/SmartTradeCard';
import MatchingOffersCard from './cards/MatchingOffersCard';
import SelectionMatchCard from './cards/SelectionMatchCard';
import AlbumTradesPanel from './AlbumTradesPanel';
import Album2Dashboard from './Album2Dashboard';
import AlbumCollectorsPanel from './AlbumCollectorsPanel';
import { useAlbumDashboard } from '../AlbumDashboardContext';
import { useAuth } from '../../hooks/useAuth';

// Chrome theme used by every card inside the sidebar so it blends with the
// rest of the app (white surfaces, blue accents) instead of inheriting the
// album's emerald variant defaults.
const SIDE_THEME = {
  cardSurface:        'var(--md-sys-color-surface, #ffffff)',
  cardSurfaceStrong:  'var(--md-sys-color-surface, #ffffff)',
  cardRowBorder:      'var(--md-sys-color-outline-variant, rgba(0,0,0,0.08))',
  cardRowHoverBg:     'var(--md-sys-color-surface-container, #f3f4f6)',
  cardHeadingText:    'var(--md-sys-color-on-surface-variant, #6b7280)',
  cardBodyText:       'var(--md-sys-color-on-surface, #111827)',
  cardSubtleText:     'var(--md-sys-color-outline, #6b7280)',
  primaryAccent:      'var(--md-sys-color-primary, #2563eb)',
  primaryAccentDim:   '#1d4ed8',
  mutedText:          'var(--md-sys-color-outline, #6b7280)',
  smartTradeCardBg:     'var(--md-sys-color-surface, #ffffff)',
  smartTradeCardBorder: 'var(--md-sys-color-outline-variant, rgba(0,0,0,0.08))',
  smartTradeButtonBg:   'var(--md-sys-color-primary, #2563eb)',
  smartTradeButtonText: '#ffffff',
  searchInputBg:      'var(--md-sys-color-surface-container, #f3f4f6)',
  searchInputText:    'var(--md-sys-color-on-surface, #111827)',
  searchIconColor:    'var(--md-sys-color-outline, #6b7280)',
  searchHoverBg:      'var(--md-sys-color-surface-container-high, #e5e7eb)',
};

function SelectedStickersCard({ selected, dispatch, theme }) {
  if (!selected || selected.size === 0) return null;
  const accent    = theme?.primaryAccent    ?? '#10B981';
  const accentDim = theme?.primaryAccentDim ?? 'rgba(16,185,129,0.14)';
  const muted     = theme?.mutedText        ?? '#6b7280';
  const sorted = [...selected].sort((a, b) => a - b);
  return (
    <section className="mb-6">
      <div className="flex items-baseline justify-between mb-3">
        <h4 className="text-xs font-bold uppercase tracking-widest" style={{ color: muted }}>
          Selected · {selected.size}
        </h4>
        <button
          onClick={() => dispatch({ type: 'CLEAR_SELECTION' })}
          className="text-[10px] font-bold uppercase tracking-wide hover:opacity-70 transition-opacity"
          style={{ color: muted }}
        >
          Clear
        </button>
      </div>
      <div className="flex flex-wrap gap-1">
        {sorted.map(n => (
          <span
            key={n}
            className="px-1.5 py-0.5 rounded text-[11px] font-bold tabular-nums"
            style={{ backgroundColor: accentDim, color: accent }}
          >
            #{n}
          </span>
        ))}
      </div>
    </section>
  );
}

function CommunityFilter({ myCommunities, selectedCommunityIds, onToggleCommunity }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!myCommunities || myCommunities.length === 0) return null;
  const selected = Array.isArray(selectedCommunityIds) ? selectedCommunityIds : [];
  const activeId = selected[0] ?? null;
  const activeName = activeId ? (myCommunities.find(c => c.id === activeId)?.name || '') : '';
  const hasFilter = activeId != null;

  return (
    <section className="mb-4 relative" ref={ref}>
      <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-2">
        {t('Filtrar por comunidad')}
      </p>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl border text-xs font-bold transition-colors ${
          hasFilter
            ? 'bg-primary/10 text-primary border-primary/40 hover:bg-primary/15'
            : 'bg-surface-container text-on-surface-variant border-outline-variant hover:bg-surface-container-high'
        }`}
      >
        <span className="flex items-center gap-2 min-w-0">
          <span className="material-symbols-outlined text-base">{hasFilter ? 'group' : 'groups'}</span>
          <span className="truncate">{hasFilter ? activeName : t('Todas las comunidades')}</span>
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {hasFilter && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onToggleCommunity?.(0); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onToggleCommunity?.(0); } }}
              className="material-symbols-outlined text-[16px] opacity-70 hover:opacity-100 cursor-pointer"
              title={t('Quitar filtro')}
            >close</span>
          )}
          <span className="material-symbols-outlined text-[18px] opacity-70">{open ? 'expand_less' : 'expand_more'}</span>
        </span>
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 mt-1 z-50 rounded-xl border border-outline-variant bg-surface shadow-2xl overflow-hidden max-h-72 overflow-y-auto"
        >
          <button
            type="button"
            onClick={() => { onToggleCommunity?.(0); setOpen(false); }}
            className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-bold text-left transition-colors ${
              !hasFilter ? 'bg-primary/10 text-primary' : 'text-on-surface-variant hover:bg-surface-container'
            }`}
          >
            <span className="material-symbols-outlined text-base">groups</span>
            <span className="flex-1">{t('Todas las comunidades')}</span>
            {!hasFilter && <span className="material-symbols-outlined text-base">check</span>}
          </button>
          <div className="h-px bg-outline-variant/40" />
          {myCommunities.map(c => {
            const active = c.id === activeId;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => { onToggleCommunity?.(c.id); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-bold text-left transition-colors ${
                  active ? 'bg-primary/10 text-primary' : 'text-on-surface hover:bg-surface-container'
                }`}
              >
                <span className="material-symbols-outlined text-base">group</span>
                <span className="flex-1 truncate" title={c.name}>{c.name}</span>
                {active && <span className="material-symbols-outlined text-base">check</span>}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

function CtaSection({ icon, title, description, ctaLabel, onClick }) {
  if (!onClick) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-surface-container p-3 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group cursor-pointer border border-transparent hover:border-primary-fixed/30 mb-4"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="material-symbols-outlined text-base text-on-surface-variant group-hover:text-primary transition-colors">{icon}</span>
        <p className="text-xs font-bold text-on-surface">{title}</p>
      </div>
      {description && (
        <p className="text-[10px] mb-2 text-on-surface-variant">{description}</p>
      )}
      <span className="text-[10px] font-bold uppercase tracking-widest text-primary group-hover:underline">
        {ctaLabel} →
      </span>
    </button>
  );
}

function PanelContent({ dispatch, myCounts, selected, viewMode, allUsers, tradeIndex, onViewUser, onCreateOffer, selectionMode, onToast, myCommunities, selectedCommunityIds, onToggleCommunity, lockedSet, incomingSet, onQuickSwap, slim, onExpandDashboard, onExpandCollectors, rightbarVariant, isMobile }) {
  const softQuickSwap = rightbarVariant === 8;
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <>
      {viewMode === 'MY_ALBUM' && selectionMode === 'panel' && (
        <SelectedStickersCard selected={selected} dispatch={dispatch} theme={SIDE_THEME} />
      )}
      <CommunityFilter
        myCommunities={myCommunities}
        selectedCommunityIds={selectedCommunityIds}
        onToggleCommunity={onToggleCommunity}
      />
      {!slim && (
        <>
          <CtaSection
            icon="campaign"
            title={t('Publicá una oferta')}
            description={t('Hacé una propuesta abierta a tu comunidad: ofrecé repetidas y pedí lo que te falta.')}
            ctaLabel={t('Crear oferta pública')}
            onClick={onCreateOffer}
          />
          <CtaSection
            icon="dashboard"
            title={t('Album Dashboard')}
            description={t('Gestioná intercambios activos, recibidos, enviados y tu historial.')}
            ctaLabel={t('Ir al dashboard')}
            onClick={() => navigate('/album/dashboard')}
          />
        </>
      )}
      {slim && (
        <AlbumTradesPanel myCommunities={myCommunities} compact variant={rightbarVariant} selectedCommunityIds={selectedCommunityIds} onExpand={onExpandDashboard} />
      )}
      {viewMode === 'MY_ALBUM' && !isMobile && (
        <SelectionMatchCard
          selected={selected}
          myCounts={myCounts}
          allUsers={allUsers}
          tradeIndex={tradeIndex}
          onViewUser={onViewUser}
          theme={SIDE_THEME}
        />
      )}
      {!slim && (
        <MatchingOffersCard allUsers={allUsers} theme={SIDE_THEME} myCommunities={myCommunities} />
      )}
      <SmartTradeCard myCounts={myCounts} allUsers={allUsers} tradeIndex={tradeIndex} onViewUser={onViewUser} theme={SIDE_THEME} onToast={onToast} myCommunities={myCommunities} lockedSet={lockedSet} incomingSet={incomingSet} onQuickSwap={onQuickSwap} softQuickSwap={softQuickSwap} />
      <UserSearchCard allUsers={allUsers} onViewUser={onViewUser} theme={SIDE_THEME} myCommunities={myCommunities} onExpand={onExpandCollectors} />
    </>
  );
}

function RightPanel({ dispatch, myCounts, selected, viewMode, allUsers, tradeIndex, onViewUser, onCreateOffer, mobileOpen, onClose, selectionMode, onToast, myCommunities, selectedCommunityIds, onToggleCommunity, lockedSet, incomingSet, onQuickSwap, rightbarVariant = 0, dashboardVariant = 0, isMobile = false }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Slim sidebar (con Album2Dashboard embebido) en /album. /album2 mantiene
  // el flow legacy con dashboard como página separada.
  const slim = !location.pathname.startsWith('/album2');
  const { expanded, expandedView, setExpanded, openExpandedView } = useAlbumDashboard();
  const isExpanded = slim && expanded;
  const isCollectorsView = isExpanded && expandedView === 'collectors';
  // Sidebar chrome stays white/neutral regardless of album variant so it
  // matches the rest of the app instead of inheriting the emerald palette.
  const bg = 'var(--md-sys-color-surface, #ffffff)';
  const borderColor = 'var(--md-sys-color-outline-variant, rgba(0,0,0,0.08))';
  const backdropFilter = undefined;

  // Lock body scroll while the mobile sheet is open
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  const content = (
    <PanelContent
      dispatch={dispatch}
      myCounts={myCounts}
      selected={selected}
      viewMode={viewMode}
      allUsers={allUsers}
      tradeIndex={tradeIndex}
      onViewUser={onViewUser}
      onCreateOffer={onCreateOffer}
      selectionMode={selectionMode}
      onToast={onToast}
      myCommunities={myCommunities}
      selectedCommunityIds={selectedCommunityIds}
      onToggleCommunity={onToggleCommunity}
      lockedSet={lockedSet}
      incomingSet={incomingSet}
      onQuickSwap={onQuickSwap}
      slim={slim}
      onExpandDashboard={slim ? (
        // En mobile el bottom-sheet drawer no soporta render expandido, así
        // que en lugar de togglear el flag (que no haría nada visible)
        // navegamos a la página fullscreen del dashboard. También cerramos
        // el drawer para que cuando el usuario vuelva con el back no quede
        // abierto en una posición inconsistente.
        isMobile
          ? () => { setExpanded(false); onClose?.(); navigate('/album/dashboard'); }
          : () => openExpandedView('dashboard')
      ) : undefined}
      onExpandCollectors={slim && !isMobile ? () => openExpandedView('collectors') : undefined}
      rightbarVariant={rightbarVariant}
      isMobile={isMobile}
    />
  );

  // Cuando expanded el aside usa flex-1 para tomar TODO el espacio que
  // sobra después de que <main> se fija al ancho de N × colW (N=cards/row).
  // Esto mantiene el ancho de las cards de país invariante, sin importar
  // viewport ni breakpoint. La medición del colW vive en StickerAlbum.js.
  const asideClass = isExpanded
    ? 'hidden tablet-sm:flex h-screen flex-col z-40 overflow-y-auto hide-scrollbar'
    : 'hidden tablet-sm:flex h-screen w-72 flex-col p-6 z-40 overflow-y-auto shrink-0 hide-scrollbar';

  return (
    <>
      {/* Pinned sidebar — tablet-md (1024px) and up */}
      <aside
        className={asideClass}
        style={{
          backgroundColor: isExpanded ? 'var(--md-sys-color-surface-container, #f3f4f6)' : bg,
          borderLeft: `1px solid ${borderColor}`,
          backdropFilter,
          WebkitBackdropFilter: backdropFilter,
          ...(isExpanded ? { flex: '1 1 0%', minWidth: 0 } : {}),
        }}
      >
        {isExpanded ? (
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-outline-variant shrink-0 bg-surface">
              <span className="text-sm font-black uppercase tracking-widest text-on-surface inline-flex items-center gap-2">
                <span className="material-symbols-outlined text-base">
                  {isCollectorsView ? 'group' : 'space_dashboard'}
                </span>
                {isCollectorsView ? t('Coleccionistas') : t('Dashboard')}
              </span>
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-on-surface-variant hover:bg-surface-container border border-outline-variant"
              >
                <span className="material-symbols-outlined text-base">unfold_less</span>
                {t('Comprimir')}
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              {isCollectorsView ? (
                <AlbumCollectorsPanel
                  allUsers={allUsers}
                  onViewUser={onViewUser}
                  myCommunities={myCommunities}
                />
              ) : (
                <Album2Dashboard
                  onClose={() => setExpanded(false)}
                  onCreateOffer={onCreateOffer}
                  myCommunities={myCommunities}
                  myUserId={user?.id ?? null}
                  dashboardVariant={dashboardVariant}
                  dispatch={dispatch}
                />
              )}
            </div>
          </div>
        ) : (
          content
        )}
      </aside>

      {/* Bottom-sheet drawer — below tablet-sm */}
      {mobileOpen && (
        <div className="tablet-sm:hidden fixed inset-0 z-[90]">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={onClose}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            className="absolute bottom-0 inset-x-0 max-h-[85vh] rounded-t-2xl shadow-2xl flex flex-col"
            style={{
              backgroundColor: bg,
              borderTop: `1px solid ${borderColor}`,
              backdropFilter,
              WebkitBackdropFilter: backdropFilter,
            }}
          >
            <div className="flex items-center justify-between px-4 pt-3 pb-2 shrink-0">
              <span className="w-10 h-1 rounded-full bg-black/20 mx-auto" aria-hidden="true" />
              <button
                onClick={onClose}
                className="absolute right-3 top-3 w-11 h-11 flex items-center justify-center rounded-full hover:bg-black/5"
                aria-label="Close"
              >
                <span className="material-symbols-outlined text-lg">close</span>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto hide-scrollbar px-4 pb-6">
              {content}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RightPanel;
